import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase/config';
import { collection, query, where, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import ModalConfirmacion from '../../../ui/ModalConfirmacion'; 

/**
 * DistribucionTab - Visualización de liquidaciones para Pólizas Patrimoniales.
 * Jerarquía % Dist: 1. Póliza -> 2. Maestros (Subcanal define Canal).
 * Jerarquía % Desc: Siempre Maestros (Ignora lo cargado en la póliza).
 */

const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);
};

const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${parseFloat(value)}%`;
};

const DistribucionTab = ({ poliza }) => {
    const [liquidaciones, setLiquidaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [formaLiquidacionReal, setFormaLiquidacionReal] = useState('-');
    
    // Reglas Frescas (Calculadas por jerarquía para cálculos en vivo)
    const [freshRules, setFreshRules] = useState(null);

    // --- 1. CARGAR REGLAS POR JERARQUÍA ---
    useEffect(() => {
        const fetchRules = async () => {
            if (!poliza) return;
            try {
                let rules = { canalPct: 0, canalDesc: 0, subPct: 0, subDesc: 0 };
                
                // --- FASE 1: PORCENTAJES GRABADOS EN LA PÓLIZA (PRIORIDAD 1) ---
                if (poliza.comisionOrganizador || poliza.comisionAsociado) {
                    rules.canalPct = parseFloat(poliza.comisionOrganizador) || 0;
                    rules.subPct = parseFloat(poliza.comisionAsociado) || 0;
                }

                // --- FASE 2: MAESTRO DE SUBCANAL (DESCUENTOS Y % FALTANTES) ---
                // Mapeo: subcanalNombreId es el nuevo nombre para vendedorAsociadoId
                const finalSubId = poliza.subcanalNombreId || poliza.vendedorAsociadoId;
                
                if (finalSubId) {
                    const subRef = doc(db, 'public/data/subcanales', finalSubId);
                    const snapSub = await getDoc(subRef);
                    
                    if (snapSub.exists()) {
                        const sData = snapSub.data();
                        
                        // Descuentos siempre del maestro
                        rules.subDesc = parseFloat(sData.patrimoniales?.descuentoSubcanal || 0);
                        rules.canalDesc = parseFloat(sData.patrimoniales?.descuentoCanal || 0);

                        // % Distribución (Solo si la póliza no los tenía grabados)
                        if (rules.subPct === 0) rules.subPct = parseFloat(sData.patrimoniales?.distribucionSubcanal || 0);
                        if (rules.canalPct === 0) rules.canalPct = parseFloat(sData.patrimoniales?.distribucionCanal || 0);
                    }
                }

                // --- FASE 3: MAESTRO DE CANAL (FORMA DE LIQUIDAR Y FALLBACKS) ---
                // Mapeo: canalNombreId es el nuevo nombre para subcanalId
                const finalCanalId = poliza.canalNombreId || poliza.subcanalId;

                if (finalCanalId) {
                    const canalRef = doc(db, 'public/data/canales', finalCanalId);
                    const snapC = await getDoc(canalRef);
                    
                    if (snapC.exists()) {
                        const cData = snapC.data();
                        setFormaLiquidacionReal(cData.formaLiquidarPatrimoniales || 'Comisión');
                        
                        // Si el descuento del canal no vino definido por el subcanal, usamos el del canal padre
                        if (rules.canalDesc === 0) rules.canalDesc = parseFloat(cData.descuento || 0);
                        
                        // Fallback final para % si todo lo anterior falló
                        if (rules.canalPct === 0) rules.canalPct = parseFloat(cData.porcentajePatrimoniales || 0);
                    }
                }

                setFreshRules(rules);

            } catch (e) { 
                console.error("Error cargando jerarquía de distribución:", e); 
            }
        };
        fetchRules();
    }, [poliza]);

    // --- 2. CARGAR LIQUIDACIONES ---
    useEffect(() => {
        const fetchLiquidaciones = async () => {
            if (!poliza.nPoliza) { setLoading(false); return; }
            setLoading(true);
            try {
                const q = query(collection(db, 'public/data/liquidaciones'), where('nPoliza', '==', poliza.nPoliza)); 
                const snapshot = await getDocs(q);
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                data.sort((a, b) => (b.fechaImportacion || '').localeCompare(a.fechaImportacion || ''));
                setLiquidaciones(data);
            } catch (error) { console.error("Error cargando liquidaciones:", error); } finally { setLoading(false); }
        };
        fetchLiquidaciones();
    }, [poliza.nPoliza]);

    // --- 3. ELIMINAR REGISTRO ---
    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteDoc(doc(db, 'public/data/liquidaciones', itemToDelete.id));
            setLiquidaciones(prev => prev.filter(l => l.id !== itemToDelete.id));
            setItemToDelete(null);
        } catch (error) { alert("Error al eliminar el registro."); }
    };

    // --- CÁLCULO VISUAL EN VIVO ---
    const calculateRow = (liq) => {
        const isPrima = formaLiquidacionReal === 'Prima';
        const base = isPrima ? (liq.importePrima || 0) : (liq.importeComision || 0);
        const r = freshRules || { canalPct: 0, canalDesc: 0, subPct: 0, subDesc: 0 }; 

        const cBruto = base * (r.canalPct / 100);
        const cNeto = cBruto - (cBruto * (r.canalDesc / 100));

        const sBruto = base * (r.subPct / 100);
        const sNeto = sBruto - (sBruto * (r.subDesc / 100));

        return { base, cBruto, cNeto, sBruto, sNeto, r };
    };

    return (
        <div className="space-y-6 animate-fade-in text-left">
            <div className="flex justify-between items-end">
                <div className="text-left">
                    <h3 className="text-lg font-bold text-gray-800 tracking-tight">Distribución de Liquidaciones</h3>
                    <p className="text-xs text-slate-500 italic mt-1">Calculado sobre la base de {formaLiquidacionReal}</p>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-[11px] text-gray-600 whitespace-nowrap">
                        <thead className="bg-slate-800 text-white uppercase font-bold tracking-tighter">
                            <tr>
                                <th colSpan="4" className="px-4 py-2.5 text-center border-r border-slate-700 bg-slate-700">Datos Importados</th>
                                <th colSpan="6" className="px-4 py-2.5 text-center border-r border-slate-700 bg-indigo-900 uppercase">
                                    DISTRIBUCIÓN CANAL ({poliza.canalNombre || poliza.subcanal || 'S/D'})
                                </th>
                                <th colSpan="6" className="px-4 py-2.5 text-center border-r border-slate-700 bg-emerald-900 uppercase">
                                    DISTRIBUCIÓN SUBCANAL ({poliza.subcanalNombre || poliza.vendedorAsociado || '-'})
                                </th>
                                <th className="px-2 py-2.5 bg-slate-800"></th>
                            </tr>
                            <tr className="bg-slate-50 text-slate-500 border-b border-slate-200 text-left">
                                <th className="px-4 py-2 border-r border-slate-200">Periodo</th>
                                <th className="px-4 py-2 text-center border-r border-slate-200 uppercase">Forma</th>
                                <th className="px-4 py-2 text-right border-r border-slate-200">Prima $</th>
                                <th className="px-4 py-2 bg-yellow-50 text-slate-900 text-right font-black border-r border-slate-300">Comisión $</th>
                                
                                <th className="px-4 py-2 border-r border-slate-100">Tipo</th>
                                <th className="px-4 py-2 border-r border-slate-100">Nombre</th>
                                <th className="px-2 py-2 text-center">% Dist</th>
                                <th className="px-2 py-2 text-center border-r border-slate-100">% Desc</th>
                                <th className="px-3 py-2 text-right border-r border-white font-semibold">Bruta $</th>
                                <th className="px-3 py-2 bg-indigo-50 text-indigo-900 text-right font-black border-r border-white">Neta $</th>

                                <th className="px-4 py-2 border-r border-slate-100">Nombre</th>
                                <th className="px-2 py-2 text-center">% Dist</th>
                                <th className="px-2 py-2 text-center border-r border-slate-100">% Desc</th>
                                <th className="px-3 py-2 text-right font-semibold border-r border-white">Bruta $</th>
                                <th className="px-3 py-2 bg-emerald-50 text-emerald-900 text-right font-black">Neta $</th>
                                
                                <th className="px-2 py-2 text-center"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-left">
                            {loading ? (
                                <tr><td colSpan="17" className="text-center py-10 font-bold text-gray-400 animate-pulse uppercase tracking-widest">Calculando distribución...</td></tr>
                            ) : liquidaciones.length === 0 ? (
                                <tr><td colSpan="17" className="text-center py-12 text-gray-400 italic">No hay liquidaciones para esta póliza.</td></tr>
                            ) : (
                                liquidaciones.map((l) => {
                                    const calc = calculateRow(l);
                                    return (
                                        <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-4 py-3 font-bold text-slate-900 border-r border-slate-100">{l.periodo}</td>
                                            <td className="px-4 py-3 text-center border-r border-slate-100 uppercase">
                                                <span className="bg-slate-100 px-1.5 py-0.5 rounded text-[9px] font-black text-slate-500 border border-slate-200">{formaLiquidacionReal}</span>
                                            </td>
                                            <td className="px-4 py-3 text-right text-slate-500 border-r border-slate-100">{formatCurrency(l.importePrima || 0)}</td>
                                            <td className="px-4 py-3 text-right font-black text-slate-900 bg-yellow-50/50 border-r border-slate-200">{formatCurrency(l.importeComision || 0)}</td>

                                            <td className="px-4 py-3 border-r border-slate-50">{poliza.canal}</td>
                                            <td className="px-4 py-3 border-r border-slate-50 truncate max-w-[120px]" title={poliza.canalNombre || poliza.subcanal}>
                                                {poliza.canalNombre || poliza.subcanal || '-'}
                                            </td>
                                            <td className="px-2 py-3 text-center text-indigo-600 font-bold">{formatPercent(calc.r.canalPct)}</td>
                                            <td className="px-2 py-3 text-center text-rose-500 border-r border-slate-50 font-bold">{calc.r.canalDesc > 0 ? `-${calc.r.canalDesc}%` : '-'}</td>
                                            <td className="px-3 py-3 text-right font-medium text-slate-600 border-r border-white">{formatCurrency(calc.cBruto)}</td>
                                            <td className="px-3 py-3 text-right font-black text-indigo-700 bg-indigo-50 border-r border-slate-100">{formatCurrency(calc.cNeto)}</td>

                                            <td className="px-4 py-3 border-r border-slate-50 truncate max-w-[120px]" title={poliza.subcanalNombre || poliza.vendedorAsociado}>
                                                {poliza.subcanalNombre || poliza.vendedorAsociado || '-'}
                                            </td>
                                            <td className="px-2 py-3 text-center text-emerald-600 font-bold">{formatPercent(calc.r.subPct)}</td>
                                            <td className="px-2 py-3 text-center text-rose-500 border-r border-slate-50 font-bold">
                                                {calc.r.subDesc > 0 ? `-${calc.r.subDesc}%` : '-'}
                                            </td>
                                            <td className="px-3 py-3 text-right font-medium text-slate-600 border-r border-white">{formatCurrency(calc.sBruto)}</td>
                                            <td className="px-3 py-3 text-right font-black text-emerald-700 bg-emerald-50">{formatCurrency(calc.sNeto)}</td>

                                            <td className="px-2 py-3 text-center">
                                                <button onClick={() => setItemToDelete(l)} className="text-slate-300 hover:text-rose-500 transition-colors">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            <ModalConfirmacion 
                show={!!itemToDelete} 
                onClose={() => setItemToDelete(null)} 
                onConfirm={handleDelete} 
                title="Eliminar Liquidación" 
                message={`¿Estás seguro de eliminar este registro del periodo ${itemToDelete?.periodo}? Esta acción no se puede deshacer.`} 
            />
        </div>
    );
};

export default DistribucionTab;